import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import flowRight from 'lodash-es/flowRight';
import orderBy from 'lodash-es/orderBy';
import uniqBy from 'lodash-es/uniqBy';
import { v4 as uuid } from 'uuid';
import { addMonths, isBefore, isValid, parseISO } from 'date-fns';
import { Formik } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import { policyType as PolicyType } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';
import { Table } from 'core';
import { AuthContext } from 'core/components/auth';
import { withStore } from 'core/store';
import { Card } from 'core/components/card';
import { Loading } from 'core/components/loading';
import Section from 'core/components/section';
import styles from './incidents.styles';
import IncidentTableRows, { IncidentTableHeader } from './incidents-table';
import IncidentsToolbar from './incidents-toolbar';
import ConfirmRemoveIncidentsDialog from './confirm-remove-incidents-dialog';
import { IgnoreDialogMode, IgnoreIncidentsDialog } from 'common/components/ignore-incidents';
import { IncidentStatus, buildAutoIncidentRows, getOtherIncidentsOnSameDates } from 'common/helpers/ignored-incidents';
import AddIncidentForm from './add-incident-form';
import {
  EMPTY_REMOVED_HOME_CLAIMS,
  buildHomeClaimSnapshots,
  getLineagePriorTermIds,
  getRemovedHomeClaims,
  parseAwsJson
} from './home-claim-history';

const formatViolation = (violation, options) => ({
  type: violation.class,
  id: uuid(),
  source: violation.source,
  addedBy: violation.addedBy,
  isCourtRecord: violation.isCourtRecord || false,
  ...options
});

const getHomeViolations = (violations) => {
  if (!violations) {
    return [];
  }

  const homeViolations = JSON.parse(violations);

  // Selects a single incident per date
  return Object.entries(homeViolations).reduce(
    (acc, [date, [homeViolation]]) =>
      homeViolation
        ? [
            ...acc,
            formatViolation(homeViolation, {
              date
            })
          ]
        : acc,
    []
  );
};

const getHomeSingleViolationDates = (violations) => {
  if (!violations) {
    return [];
  }

  const homeViolations = JSON.parse(violations);
  // TODO: clarify type to be used for major and minor violations

  return [
    ...Object.entries(homeViolations.MIN).map(([date, violation]) => formatViolation(violation, { date, type: 'MIN' })),
    ...Object.entries(homeViolations.MAJ).map(([date, violation]) => formatViolation(violation, { date, type: 'MAJ' }))
  ];
};

const getHomeClaims = (claims) => {
  if (!claims) {
    return [];
  }

  try {
    const homeClaims = JSON.parse(claims);

    return Object.entries(homeClaims).reduce(
      (acc, [date, claimList]) =>
        claimList?.length
          ? [...acc, ...claimList.map((claim) => formatViolation(claim, { date, category: 'claim' }))]
          : acc,
      []
    );
  } catch {
    console.error('[incidents] Failed to parse homeClaimDates');
    return [];
  }
};

// homeClaimDates only carries claims under 36 months old when the reports were pulled, so the
// Verisk A-PLUS list kept on the segment for UW (homeClaimsForUW, 60 months) is the only place older
// losses can be shown. These rows are display-only: nothing rates on them, and they can't be removed.
// Both windows are measured from the term's effective date, the date that term was rated and
// underwritten as of, so the list stays the same for the whole term. Claims UW removed are left out
// for good; see home-claim-history.js for how they are found (#36608).
const RATED_CLAIM_LOOKBACK_MONTHS = 36;
const UW_CLAIM_LOOKBACK_MONTHS = 60;

// Verisk sends lossDate as YYYYMMDD; claims added by staff are already YYYY-MM-DD
const normalizeLossDate = (lossDate) => {
  const dashed = /^\d{8}$/.test(lossDate || '')
    ? `${lossDate.slice(0, 4)}-${lossDate.slice(4, 6)}-${lossDate.slice(6, 8)}`
    : lossDate?.slice(0, 10);
  return dashed && isValid(parseISO(dashed)) ? dashed : null;
};

export const getNotRatedHomeClaims = (policy, { removedClaims = EMPTY_REMOVED_HOME_CLAIMS } = {}) => {
  const termEffectiveDate = policy?.effectiveDate && parseISO(policy.effectiveDate);
  if (!termEffectiveDate || !isValid(termEffectiveDate)) {
    return [];
  }

  const segments = policy.segments || [];
  const ratedClaimDates = segments.map((segment) => parseAwsJson(segment.homeClaimDates, {}));
  const ratedClaimKeys = new Set(
    ratedClaimDates.flatMap((claimDates) =>
      Object.entries(claimDates).flatMap(([date, claimList]) =>
        (claimList || []).map((claim) => `${date}|${claim.class}`)
      )
    )
  );
  const ratedDates = new Set(ratedClaimDates.flatMap((claimDates) => Object.keys(claimDates)));

  // Policies quoted before claimGroup was stored on these entries still carry some without one; they
  // show under their Verisk loss type, and can only be matched to a rated claim by date
  const isRated = ({ date, claimGroup }) =>
    claimGroup ? ratedClaimKeys.has(`${date}|${claimGroup}`) : ratedDates.has(date);
  const wasRemoved = ({ date, claimGroup }) =>
    claimGroup ? removedClaims.keys.has(`${date}|${claimGroup}`) : removedClaims.dates.has(date);

  const notRatedClaims = segments
    .flatMap((segment) => {
      const claims = parseAwsJson(segment.homeClaimsForUW, []);
      return Array.isArray(claims) ? claims : [];
    })
    .map((claim) => ({
      date: normalizeLossDate(claim?.lossDate),
      claimGroup: claim?.claimGroup,
      type: claim?.claimGroup || claim?.lossTypeCode
    }))
    .filter((claim) => {
      if (!claim.date || !claim.type || isRated(claim) || wasRemoved(claim)) {
        return false;
      }
      const lossDate = parseISO(claim.date);
      return (
        !isBefore(termEffectiveDate, addMonths(lossDate, RATED_CLAIM_LOOKBACK_MONTHS)) &&
        isBefore(termEffectiveDate, addMonths(lossDate, UW_CLAIM_LOOKBACK_MONTHS))
      );
    });

  // the same loss is often stored several times over, and again on every segment. One row per
  // date and class, the same key the rated claim rows use
  return uniqBy(notRatedClaims, ({ date, type }) => `${date}|${type}`).map(({ date, type }) =>
    formatViolation({ class: type }, { date, category: 'claim', notRated: true })
  );
};

const getHomeIncidents = (policy) =>
  policy?.segments?.reduce(
    (acc, segment) => [
      ...acc,
      ...getHomeViolations(segment.homeViolationDates),
      ...getHomeSingleViolationDates(segment.homeSingleViolationDates),
      ...getHomeClaims(segment.homeClaimDates)
    ],
    []
  );

// The not-rated rows are Branch-only: the mutuals' raters can price on homeClaimsForUW (Oakwood's
// claim-free discount reads it over 5 years), so "not rated" would be wrong there. They also wait
// for the removal history: `removedClaims` is null while it loads or if it failed, and showing the
// rows then could bring back a claim UW removed
export const getHomePolicyIncidents = (policy, removedClaims) => [
  ...(getHomeIncidents(policy) || []),
  ...(appConfig?.featureFlags?.showNotRatedHomeClaims && removedClaims
    ? getNotRatedHomeClaims(policy, { removedClaims })
    : [])
];

// Every claim and violation on every date, with its status (#36871). A row's id is driver + date + class,
// so the latest segment's copy (and status) of an incident replaces an earlier segment's.
export const getAutoPolicyIncidents = (policy) => [
  ...new Map(
    (policy?.segments || []).flatMap((segment) =>
      buildAutoIncidentRows(segment?.drivers, {
        countsEveryTieredViolation: !!appConfig?.featureFlags?.countsEveryTieredAutoViolation,
        effectiveDate: segment?.global?.autoEffectiveDate || policy?.effectiveDate,
        state: policy?.state
      }).map((row) => [row.id, row])
    )
  ).values()
];

// Every policy segment carries a full driver (auto) / coverage (home) snapshot, so the
// same underlying incident is re-emitted once per segment that carries it. Collapse those
// repeats to a single row, keyed on every field except `id`, which is a per-render uuid
// (see formatViolation). Removal keys off date/type (see handleConfirmRemove), which
// dedupe preserves.
export const dedupeIncidents = (incidents) => uniqBy(incidents, ({ id, ...rest }) => JSON.stringify(rest));

const Incidents = ({
  loadingPreview,
  handleChangePolicy,
  onAddIncident,
  onIgnoreIncidents,
  classes,
  store: {
    account: {
      policies: {
        list: policiesList,
        policy: { loading, policy, violationsForRenewal, getHomeClaimHistory }
      }
    }
  }
}) => {
  const session = useContext(AuthContext);
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedIncidents, setSelectedIncidents] = useState({});
  const [incidentsToRemove, setIncidentsToRemove] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [ignoreDialog, setIgnoreDialog] = useState(null);

  const canScrub = session?.canScrubIncidents;

  const canRemove = !!canScrub && !!session?.isService && !session?.viewOnly;

  // canEditPolicies mirrors confirmPolicyChange's Cognito allowlist (and covers viewOnly),
  // so an AddIncidents-only user is never offered a preview they can't confirm
  const canAdd =
    session?.canAddIncidents &&
    session?.canEditPolicies &&
    [PolicyType.Auto, PolicyType.Home, PolicyType.Condo].includes(policy.policyType);

  const isAutoPolicyType = policy.policyType === PolicyType.Auto;

  // Underwriters ignore auto claims and violations (#36871); canEditPolicies mirrors the confirm allowlist
  const canIgnore = isAutoPolicyType && !!session?.canApproveUWReview && !!session?.canEditPolicies;
  const canSelect = canRemove || canIgnore;
  const isSelectable = useCallback(
    (incident) => !incident.notRated && (canRemove || (canIgnore && incident.canIgnore)),
    [canRemove, canIgnore]
  );

  // Earlier terms' homeClaimDates, to tell a claim UW removed from one that aged out (#36608)
  const showNotRatedHomeClaims = !!appConfig?.featureFlags?.showNotRatedHomeClaims && !isAutoPolicyType;
  const priorTermIds = useMemo(
    () => (showNotRatedHomeClaims ? getLineagePriorTermIds(policy, policiesList) : []),
    [showNotRatedHomeClaims, policy, policiesList]
  );
  const [removedHomeClaims, setRemovedHomeClaims] = useState(null);
  useEffect(() => {
    if (!showNotRatedHomeClaims || !policy?.id) {
      return undefined;
    }
    let cancelled = false;
    setRemovedHomeClaims(null);
    const loadRemovedHomeClaims = async () => {
      const { ok, terms } = priorTermIds.length
        ? // policy ids start with the account id; get-policy reads the policy by id alone
          await getHomeClaimHistory(priorTermIds, policy.id.split('-')[0])
        : { ok: true, terms: [] };
      if (!cancelled && ok) {
        setRemovedHomeClaims(getRemovedHomeClaims(buildHomeClaimSnapshots([...terms, policy])));
      }
    };
    loadRemovedHomeClaims();
    return () => {
      cancelled = true;
    };
  }, [showNotRatedHomeClaims, policy, priorTermIds, getHomeClaimHistory]);

  const currentDrivers = useMemo(() => {
    if (!isAutoPolicyType || !policy?.segments?.length) {
      return [];
    }
    return policy.segments[policy.segments.length - 1].drivers || [];
  }, [policy, isAutoPolicyType]);

  const rawIncidents = useMemo(() => {
    if (!policy) {
      return [];
    }

    const assembled =
      policy.policyType === PolicyType.Auto
        ? getAutoPolicyIncidents(policy)
        : getHomePolicyIncidents(policy, removedHomeClaims);
    return dedupeIncidents(assembled);
  }, [policy, removedHomeClaims]);

  const incidents = useMemo(
    () => orderBy(rawIncidents, ['date', 'driver'], [sortDirection, 'asc']),
    [rawIncidents, sortDirection]
  );

  const sort = (_columnId, _active, direction) => setSortDirection(direction);

  const selectIncident = (incident, checked) => {
    setSelectedIncidents((prev) => {
      if (checked) {
        return { ...prev, [incident.id]: incident };
      }

      const { [incident.id]: currentIncident, ...others } = prev;

      return others;
    });
  };

  const handleConfirmRemove = useCallback(() => {
    const claims = incidentsToRemove.filter((item) => item.category === 'claim');
    const violations = incidentsToRemove.filter((item) => item.category !== 'claim');
    const violationDates = violations.map((item) => item.date);
    const claimsToScrub = claims.map((item) => ({ date: item.date, claimClass: item.type }));
    // Fire the mutation first so the preview modal opens before we close the confirm dialog,
    // preventing a white flash between the two modals
    handleChangePolicy({ immediateBillOrRefundAmount: false, id: policy.id }, violationDates, claimsToScrub);
    setConfirmDialogOpen(false);
    setIncidentsToRemove([]);
    setSelectedIncidents({});
  }, [incidentsToRemove, policy, handleChangePolicy]);

  const handleCloseConfirmDialog = useCallback(() => {
    setConfirmDialogOpen(false);
    setIncidentsToRemove([]);
  }, []);

  const handleAddIncident = useCallback(
    (incidentsToAdd) => {
      onAddIncident({ immediateBillOrRefundAmount: false, id: policy.id }, incidentsToAdd);
    },
    [onAddIncident, policy]
  );

  const handleChange = useCallback(() => {
    const selected = Object.values(selectedIncidents);
    // a removal deletes every auto incident on the date, so the dialog lists all of them
    const selectedDates = new Set(selected.map((incident) => incident.date));
    setIncidentsToRemove(
      isAutoPolicyType ? incidents.filter((incident) => selectedDates.has(incident.date)) : selected
    );
    setConfirmDialogOpen(true);
  }, [selectedIncidents, isAutoPolicyType, incidents]);

  const selectedRows = Object.values(selectedIncidents);
  const rowsToIgnore = selectedRows.filter(
    (incident) => incident.canIgnore && incident.status !== IncidentStatus.Ignored
  );
  const rowsToStopIgnoring = selectedRows.filter((incident) => incident.status === IncidentStatus.Ignored);

  const handleConfirmIgnore = useCallback(
    (rows) => {
      const policyData = { immediateBillOrRefundAmount: false, id: policy.id };
      // Fire the mutation first so the preview modal opens before the dialog closes (see handleConfirmRemove)
      onIgnoreIncidents(
        policyData,
        ignoreDialog.mode === IgnoreDialogMode.Ignore
          ? { ignore: rows.map(({ driverId, date, type }) => ({ driverId, date, class: type })) }
          : { stopIgnoring: rows.map(({ driverId, date, type }) => ({ driverId, date, class: type })) }
      );
      setIgnoreDialog(null);
      setSelectedIncidents({});
    },
    [ignoreDialog, policy, onIgnoreIncidents]
  );

  const [futureIncidentsSortDirection, setFutureIncidentsSortDirection] = useState('asc');

  const orderedFutureIncidents = useMemo(
    () => orderBy(violationsForRenewal, ['date'], [futureIncidentsSortDirection]),
    [violationsForRenewal, futureIncidentsSortDirection]
  );

  return (
    <Formik
      initialValues={{
        checkbox: []
      }}
      onSubmit={handleChange}
    >
      {({ submitForm }) => (
        <>
          <div>
            <Section title="Current Incidents & Violations">
              {loading ? (
                <Loading type="secondary" />
              ) : (
                <>
                  <Card className={classes.tableContainer} type="secondary">
                    {incidents.length > 0 && (canIgnore || canRemove) && (
                      <IncidentsToolbar
                        selectedCount={selectedRows.length}
                        ignoreCount={rowsToIgnore.length}
                        stopIgnoringCount={rowsToStopIgnoring.length}
                        showIgnore={canIgnore}
                        showRemove={canRemove}
                        disabled={!!loadingPreview}
                        onIgnore={() => setIgnoreDialog({ mode: IgnoreDialogMode.Ignore, incidents: rowsToIgnore })}
                        onStopIgnoring={() =>
                          setIgnoreDialog({ mode: IgnoreDialogMode.StopIgnoring, incidents: rowsToStopIgnoring })
                        }
                        onRemove={submitForm}
                        onClear={() => setSelectedIncidents({})}
                      />
                    )}
                    {incidents.length > 0 ? (
                      <Table
                        mode={'dark'}
                        dataCy="incidents"
                        selfContained={false}
                        header={
                          <IncidentTableHeader
                            canSelect={canSelect}
                            isAutoPolicy={isAutoPolicyType}
                            onSort={sort}
                            classes={classes}
                          />
                        }
                        body={
                          <IncidentTableRows
                            canSelect={canSelect}
                            isSelectable={isSelectable}
                            selectedIncidents={selectedIncidents}
                            incidents={incidents}
                            classes={classes}
                            isAutoPolicy={isAutoPolicyType}
                            onSelect={selectIncident}
                          />
                        }
                      />
                    ) : (
                      <p> No Incidents</p>
                    )}
                  </Card>
                </>
              )}
              {canAdd && !loading && (
                <div className={classes.addIncidentContainer}>
                  <AddIncidentForm
                    isAutoPolicy={isAutoPolicyType}
                    drivers={currentDrivers}
                    onSubmit={handleAddIncident}
                    loading={loadingPreview}
                  />
                </div>
              )}
            </Section>
          </div>
          {violationsForRenewal.length > 0 && (
            <Section title="Future Term Incidents & Violations">
              {loading ? (
                <Loading type="secondary" />
              ) : (
                <Card className={classes.tableContainer} type="secondary">
                  <Table
                    mode={'dark'}
                    dataCy="incidents"
                    selfContained={false}
                    header={
                      <IncidentTableHeader
                        canSelect={false}
                        isAutoPolicy={isAutoPolicyType}
                        onSort={(_columnId, _active, direction) => setFutureIncidentsSortDirection(direction)}
                        classes={classes}
                      />
                    }
                    body={
                      <IncidentTableRows
                        canSelect={false}
                        incidents={orderedFutureIncidents}
                        classes={classes}
                        isAutoPolicy={isAutoPolicyType}
                      />
                    }
                  />
                </Card>
              )}
            </Section>
          )}
          {ignoreDialog && (
            <IgnoreIncidentsDialog
              mode={ignoreDialog.mode}
              incidents={ignoreDialog.incidents}
              otherIncidentsOnDates={getOtherIncidentsOnSameDates(incidents, ignoreDialog.incidents, {
                stopIgnoring: ignoreDialog.mode === IgnoreDialogMode.StopIgnoring
              })}
              everyTieredViolationCounts={!!appConfig?.featureFlags?.countsEveryTieredAutoViolation}
              confirmLabel="Preview change"
              onConfirm={handleConfirmIgnore}
              onClose={() => setIgnoreDialog(null)}
            />
          )}
          {confirmDialogOpen && incidentsToRemove.length > 0 && (
            <ConfirmRemoveIncidentsDialog
              open={confirmDialogOpen}
              incidents={incidentsToRemove}
              isAutoPolicy={isAutoPolicyType}
              onConfirm={handleConfirmRemove}
              onClose={handleCloseConfirmDialog}
            />
          )}
        </>
      )}
    </Formik>
  );
};

Incidents.propTypes = {
  classes: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  loadingPreview: PropTypes.bool.isRequired,
  handleChangePolicy: PropTypes.func.isRequired,
  onAddIncident: PropTypes.func.isRequired,
  onIgnoreIncidents: PropTypes.func.isRequired
};

export default flowRight(withStyles(styles), withStore)(Incidents);
